/**
 * @file Récap par étape pour le breadcrumb de réservation.
 * @module packages.common.components.table-reservation.utils.stepSummary
 *
 * Fournit une chaîne courte résumant la valeur choisie pour chaque étape du
 * parcours, à afficher comme sous-titre (desktop) ou contenu de chip (mobile)
 * dans le breadcrumb. Centralise toute la logique de formatage : aucun appel
 * dispersé dans le composant. Retourne `null` pour les étapes sans récap
 * pertinent (DETAILS, PAYMENT), ce qui permet au composant d'omettre la
 * ligne de sous-titre sans condition supplémentaire.
 */

import {format} from 'date-fns';
import {BASE_STEPS} from '../constants/steps';

/**
 * Formate une date courte selon la locale du navigateur (ex. "12 mai", "12 May").
 *
 * @param {Date|string|null|undefined} value - Date à formater
 * @return {string|null} Représentation jour+mois courte, ou `null` si invalide
 */
function formatShortDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {day: 'numeric', month: 'short'});
}

/**
 * Capitalise la première lettre d'une chaîne (laisse le reste tel quel).
 *
 * @param {string} str - Chaîne à capitaliser
 * @return {string} Chaîne capitalisée, ou la valeur d'entrée si non exploitable
 */
function capitalizeFirst(str) {
  if (!str || typeof str !== 'string') return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Libellé complet d'un créneau tel que le client l'a choisi : « Jeudi 2 octobre à 19:30 ».
 *
 * Partagé entre l'écran de confirmation et l'écran « créneau perdu » pour
 * que le parcours décrive toujours le créneau de la même façon.
 *
 * @param {Date|string} date - Jour choisi
 * @param {string} timeSlot - Heure « HH:mm »
 * @param {Object} options - Localisation
 * @param {Object} [options.locale] - Locale date-fns courante
 * @param {Function} [options.t] - Fonction de traduction (mot « à »)
 * @return {string} Libellé, ou chaîne vide sans date exploitable ou sans heure
 */
export function formatSlotLabel(date, timeSlot, {locale, t}) {
  if (!date || !timeSlot) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const day = capitalizeFirst(format(d, 'EEEE d MMMM', {locale}));
  const at = t ? t('widgets.reservation.confirmation.at') : 'à';
  return `${day} ${at} ${timeSlot}`;
}

/**
 * Compte total des formules sélectionnées (somme des quantités).
 *
 * @param {Array} formulas - Liste d'objets `{formulaId, quantity}` ; `quantity` défaut à 1.
 * @return {number}
 */
function totalFormulasQuantity(formulas) {
  if (!Array.isArray(formulas)) return 0;
  return formulas.reduce((acc, f) => acc + (f.quantity || 1), 0);
}

/**
 * Retourne la chaîne récap à afficher pour une étape donnée.
 *
 * Les étapes sans valeur sélectionnée ou sans récap pertinent renvoient `null`,
 * ce qui indique au composant de ne pas rendre la ligne de sous-titre.
 *
 * @param {string} stepKey - Identifiant logique de l'étape (BASE_STEPS.X)
 * @param {Object} formData - État courant du formulaire de réservation
 * @return {string|null} Récap formaté, ou `null` si non pertinent / non rempli
 */
export function getStepSummary(stepKey, formData) {
  switch (stepKey) {
    case BASE_STEPS.SERVICE_GUESTS: {
      const {service, partySize} = formData;
      if (!service || !partySize) return null;
      return `${capitalizeFirst(service)} • ${partySize} pers.`;
    }

    case BASE_STEPS.DATE:
      return capitalizeFirst(formatShortDate(formData.date));

    case BASE_STEPS.ROOM: {
      const room = (formData.availableRooms || []).find((r) => r.id === formData.selectedAreaId);
      return room ? room.name : null;
    }

    case BASE_STEPS.TIME:
      return formData.timeSlot || null;

    case BASE_STEPS.FORMULA: {
      const count = totalFormulasQuantity(formData.selectedFormulas);
      if (count === 0) return null;
      return `${count} formule${count > 1 ? 's' : ''}`;
    }

    case BASE_STEPS.CONTACT: {
      const {firstName, lastName} = formData;
      const parts = [firstName, lastName]
          .filter(Boolean)
          .map((s) => capitalizeFirst(s.trim()));
      return parts.length ? parts.join(' ') : null;
    }

    case BASE_STEPS.DETAILS:
    case BASE_STEPS.PAYMENT:
    default:
      return null;
  }
}

/**
 * Ordre logique global des étapes du parcours de réservation.
 *
 * Utilisé pour comparer deux étapes (savoir si l'une est plus avancée que
 * l'autre) lors du tracking de `maxStepReached`. Indépendant de
 * `availableSteps.steps` qui peut omettre des étapes conditionnelles.
 */
export const STEP_ORDER = [
  BASE_STEPS.SERVICE_GUESTS,
  BASE_STEPS.DATE,
  BASE_STEPS.ROOM,
  BASE_STEPS.TIME,
  BASE_STEPS.FORMULA,
  BASE_STEPS.CONTACT,
  BASE_STEPS.DETAILS,
  BASE_STEPS.PAYMENT,
  BASE_STEPS.CONFIRMATION,
];

/**
 * Retourne l'étape la plus avancée entre deux, selon `STEP_ORDER`.
 *
 * @param {string} a - Étape candidate A
 * @param {string} b - Étape candidate B
 * @return {string} Celle qui est plus loin dans le parcours
 */
export function maxStep(a, b) {
  const ia = STEP_ORDER.indexOf(a);
  const ib = STEP_ORDER.indexOf(b);
  return ia >= ib ? a : b;
}
